import { Announcement, Button, Div, Modal } from '@vgrubs/components';
import {
  AnnouncementStep,
  LAST_ANNOUNCEMENT_STEP,
} from '../helpers/announcementSteps';
import { useAnnouncementForm } from '../hooks/useAnnouncementForm';
import { AnnouncementAudienceStep } from './AnnouncementAudienceStep';
import { AnnouncementContentStep } from './AnnouncementContentStep';
import { AnnouncementReviewStep } from './AnnouncementReviewStep';
import { AnnouncementScheduleStep } from './AnnouncementScheduleStep';
import { AnnouncementStepBar } from './AnnouncementStepBar';

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  /** The announcement being edited, or null to create a new one. */
  announcement: Announcement | null;
  /** Called after a successful create or update, so the list can refresh. */
  onSaved: () => void;
};

/**
 * Create and edit, as four steps.
 *
 * It was one long form, and the audience part of it - a group per image, a
 * chip per platform - buried the two fields anyone actually changes. Splitting
 * it also gives the last step somewhere to put the preview, which is the only
 * place the markdown body stops being a text area.
 */
export const AnnouncementFormModal = ({
  isOpen,
  closeModal,
  announcement,
  onSaved,
}: Props) => {
  const {
    step,
    draft,
    update,
    audience,
    saving,
    goBack,
    goNext,
    goTo,
    submit,
    blocker,
  } = useAnnouncementForm({
    announcement,
    isOpen,
    onSaved: () => {
      onSaved();
      closeModal();
    },
  });

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      logo={false}
      header={announcement ? 'Edit Announcement' : 'New Announcement'}
      modalOverflow="visible"
      width="550px"
      /* Desktop only: one size on every step, instead of resizing around two
         fields on one and a stack of groups on the next. The modal's own
         scroller handles a step with more to say, and the cap keeps the
         footer on screen on a short laptop.

         A phone gets neither. 650px is most of that screen, and the steps are
         a single column there anyway, so it grows with its content as usual. */
      minHeight="min(650px, calc(90vh - 85px))"
      maxHeight="min(650px, calc(90vh - 85px))"
      minHeightSM="calc(90vh - 85px)"
      maxHeightSM="calc(90vh - 85px)"
      footer={
        <Div $flex $gap="16px" $width="100%">
          {/* The first step has nowhere to go back to, so its gray button
              cancels instead - and every step keeps the same two-button row. */}
          {step > 0 ? (
            <Button $selected $gray $w="100%" onClick={goBack}>
              Back
            </Button>
          ) : (
            <Button $selected $gray $w="100%" onClick={closeModal}>
              Cancel
            </Button>
          )}
          {step < LAST_ANNOUNCEMENT_STEP ? (
            <Button $selected $w="100%" onClick={goNext}>
              Next
            </Button>
          ) : (
            <Button
              $selected
              $w="100%"
              $loading={saving}
              /* Held shut while anything is missing - an image above all,
                 which the API rejects the whole request over. The review
                 step prints the reason under the button. */
              disabled={saving || !!blocker}
              onClick={submit}
            >
              {announcement ? 'Save' : 'Create'}
            </Button>
          )}
        </Div>
      }
      /* Always one row: Back and Next sit side by side, so the space the
         footer needs is the same on every step. Letting it follow the button
         count moved the floor by 60px between step one and step two. */
      $numOfFloatingButtons={1}
    >
      <AnnouncementStepBar current={step} onStepClick={goTo} />

      {step === AnnouncementStep.content && (
        <AnnouncementContentStep draft={draft} onChange={update} />
      )}

      {step === AnnouncementStep.audience && (
        <AnnouncementAudienceStep
          groups={audience.groups}
          onPatch={audience.patchGroup}
          onToggleChip={audience.toggleChip}
          onAdd={audience.addGroup}
          onRemove={audience.removeGroup}
          groupsMissingMedia={audience.groupsMissingMedia}
        />
      )}

      {step === AnnouncementStep.schedule && (
        <AnnouncementScheduleStep draft={draft} onChange={update} />
      )}

      {step === AnnouncementStep.review && (
        <AnnouncementReviewStep
          draft={draft}
          groups={audience.groups}
          selection={audience.selection}
          blocker={blocker}
        />
      )}
    </Modal>
  );
};
